import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  getChamadosControllerListarQueryKey,
  getChamadosControllerResumoQueryKey,
  useChamadosControllerAbrir,
} from '@/api/generated/suporte/suporte';
import type { ChamadoResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  abrirChamadoSchema,
  type FormularioDeChamado as Valores,
} from '@/features/suporte/schemas/chamado.schema';
import { DICA_URGENCIA, ROTULO_URGENCIA, URGENCIAS } from '@/features/suporte/rotulos';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { toast } from '@/shared/ui/sonner';

/**
 * As quatro perguntas da abertura de chamado.
 *
 * Renderizado inline em DOIS lugares — dentro do painel do botão flutuante e
 * dentro do `NovoChamadoDialogo` — e é por isso que ele não é um diálogo em si:
 * os dois pontos de entrada exigidos precisam fazer exatamente a mesma
 * pergunta, e duas cópias divergiriam no primeiro campo novo.
 */
export function FormularioDeChamado({
  aoConcluir,
  aoCancelar,
  compacto,
}: {
  aoConcluir: (chamado: ChamadoResponse) => void;
  aoCancelar?: () => void;
  /** Versão do painel flutuante: menos respiro, sem a dica longa de urgência. */
  compacto?: boolean;
}) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const abrir = useChamadosControllerAbrir();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Valores>({
    resolver: zodResolver(abrirChamadoSchema),
    defaultValues: { titulo: '', descricao: '', telefoneContato: '', urgencia: 'MEDIA' },
  });

  // Telefone do perfil como ponto de partida, editável: na maior parte das
  // vezes é o número certo, e quando não é a pessoa troca. O backend grava um
  // SNAPSHOT do que for enviado — mudar o perfil depois não reescreve o
  // chamado.
  useEffect(() => {
    if (me?.perfil.telefone) setValue('telefoneContato', me.perfil.telefone);
  }, [me?.perfil.telefone, setValue]);

  const urgencia = watch('urgencia');

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      const chamado = await abrir.mutateAsync({ data: dados });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getChamadosControllerListarQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getChamadosControllerResumoQueryKey() }),
      ]);

      toast.success(`Chamado ${chamado.codigo} aberto. Já avisamos o suporte.`);
      reset();
      aoConcluir(chamado);
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível abrir o chamado.');
    }
  });

  return (
    <form onSubmit={(e) => void aoEnviar(e)} className={compacto ? 'space-y-3' : 'space-y-4'}>
      {errors.root && (
        <Alert variant="destructive">
          <AlertDescription>{errors.root.message}</AlertDescription>
        </Alert>
      )}

      <Campo id="titulo" rotulo="Qual é o problema?" erro={errors.titulo?.message} obrigatorio>
        <Input
          id="titulo"
          placeholder="Ex.: não consigo assinar o receituário"
          className={CLASSE_CONTROLE}
          {...register('titulo')}
        />
      </Campo>

      <Campo
        id="descricao"
        rotulo="Conte o que aconteceu"
        erro={errors.descricao?.message}
        dica="O que você fez, o que esperava e o que apareceu na tela."
        obrigatorio
      >
        <Textarea
          id="descricao"
          rows={compacto ? 3 : 5}
          placeholder="Clico em assinar e a tela fica carregando para sempre."
          className={CLASSE_CONTROLE}
          {...register('descricao')}
        />
      </Campo>

      <Campo
        id="telefoneContato"
        rotulo="Telefone para contato"
        erro={errors.telefoneContato?.message}
        dica="Usamos só se precisarmos tirar uma dúvida sobre o chamado."
        obrigatorio
      >
        <Input
          id="telefoneContato"
          inputMode="tel"
          placeholder="(62) 99999-8888"
          className={CLASSE_CONTROLE}
          {...register('telefoneContato')}
        />
      </Campo>

      <Campo
        id="urgencia"
        rotulo="Urgência"
        erro={errors.urgencia?.message}
        dica={compacto ? undefined : DICA_URGENCIA[urgencia]}
        obrigatorio
      >
        <Select value={urgencia} onValueChange={(v) => setValue('urgencia', v as Valores['urgencia'])}>
          <SelectTrigger id="urgencia" className={CLASSE_CONTROLE}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {URGENCIAS.map((nivel) => (
              <SelectItem key={nivel} value={nivel}>
                {ROTULO_URGENCIA[nivel]} — {DICA_URGENCIA[nivel]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Campo>

      <div className="flex gap-2 pt-1">
        {aoCancelar && (
          <Button type="button" variant="outline" onClick={aoCancelar} className="flex-1">
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting} className="flex-1">
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Abrir chamado
        </Button>
      </div>
    </form>
  );
}
